import { useEffect, useState } from "preact/hooks";
import type { Gender } from "../shared/styles";
import { byIndex, type Member, type Result, uid } from "./lib/db";
import { go } from "./nav";
import { saveMember, thumbUrl, useStore } from "./state";
import { Icon, Sheet, Silhouette } from "./ui";

export function Home({ status }: { status: { ok: boolean } | null }) {
  const members = useStore((s) => s.members);
  const [adding, setAdding] = useState(false);

  return (
    <div class="page home">
      <header class="topbar">
        <div class="brand">
          <span class="brand-zh">剪前</span>
          <span class="brand-en">before the cut</span>
        </div>
        <button class="icon-btn" aria-label="设置" onClick={() => window.dispatchEvent(new CustomEvent("jq:settings"))}>
          <Icon name="settings" />
        </button>
      </header>

      <section class="hero">
        <h1>
          剪之前，
          <br />
          先看看合不合适。
        </h1>
        <p class="muted">录一张照片，男款女款几十种发型随手切换。脸永远是原图的脸，只换头发。</p>
      </section>

      {status && !status.ok && (
        <div class="notice">
          <Icon name="sparkle" size={18} />
          <span>服务端还没配置生图模型的 Key，照片可以先录入，发型暂时生成不了。</span>
        </div>
      )}

      <section class="members">
        <div class="section-head">
          <h2>家人</h2>
          <span class="muted">{members.length ? `${members.length} 位` : ""}</span>
        </div>
        <div class="member-grid">
          {members.map((m) => (
            <MemberCard key={m.id} member={m} />
          ))}
          <button class="member-card add" onClick={() => setAdding(true)}>
            <span class="add-circle">
              <Icon name="plus" size={26} />
            </span>
            <span class="member-name">{members.length ? "添加家人" : "从自己开始"}</span>
          </button>
        </div>
      </section>

      <section class="howto">
        <h2>怎么拍效果最好</h2>
        <ol>
          <li>
            <b>正对镜头</b>，表情自然，额头和发际线露出来。
          </li>
          <li>
            <b>光线均匀</b>，白天窗边最好，避免顶光和逆光。
          </li>
          <li>
            <b>不戴帽子和眼镜</b>，头发尽量别挡脸。
          </li>
          <li>
            想看侧面，<b>再补一张侧脸和一张 45° 斜侧</b>。
          </li>
        </ol>
      </section>

      <MemberForm open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function MemberCard({ member }: { member: Member }) {
  const [cover, setCover] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    void byIndex<Result>("results", "memberId", member.id).then((list) => {
      if (!alive) return;
      setCount(list.length);
      const fav = list.find((r) => r.id === member.coverResultId) ?? list.find((r) => r.favorite) ?? list.at(-1);
      setCover(fav ? thumbUrl(fav) : null);
    });
    return () => {
      alive = false;
    };
  }, [member.id, member.coverResultId]);

  return (
    <button class="member-card" onClick={() => go(`/m/${member.id}`)}>
      <span class={`avatar ${member.gender}`}>
        {cover ? (
          <img src={cover} alt="" />
        ) : (
          <Silhouette type={member.gender === "female" ? "lob" : "crop"} gender={member.gender} />
        )}
      </span>
      <span class="member-name">{member.name}</span>
      <span class="member-meta">{count ? `试过 ${count} 款` : "还没试"}</span>
    </button>
  );
}

export function MemberForm({ open, onClose, member }: { open: boolean; onClose: () => void; member?: Member }) {
  const [name, setName] = useState(member?.name ?? "");
  const [gender, setGender] = useState<Gender>(member?.gender ?? "female");
  useEffect(() => {
    if (open) {
      setName(member?.name ?? "");
      setGender(member?.gender ?? "female");
    }
  }, [open, member]);

  const save = async (e: Event) => {
    e.preventDefault();
    const m: Member = member
      ? { ...member, name: name.trim(), gender }
      : { id: uid(), name: name.trim(), gender, createdAt: Date.now() };
    await saveMember(m);
    onClose();
    if (!member) go(`/m/${m.id}`);
  };

  return (
    <Sheet open={open} onClose={onClose} title={member ? "编辑" : "添加家人"}>
      <form class="form" onSubmit={save}>
        <label class="label">怎么称呼</label>
        <input
          class="field"
          placeholder="比如：我、老婆、妈妈"
          value={name}
          maxLength={12}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
        />
        <label class="label">默认发型库</label>
        <div class="seg">
          {(["female", "male"] as Gender[]).map((g) => (
            <button type="button" class={gender === g ? "on" : ""} onClick={() => setGender(g)}>
              {g === "female" ? "女款" : "男款"}
            </button>
          ))}
        </div>
        <p class="fine">只决定默认显示哪一边，之后两边的发型都能试。</p>
        <button class="btn primary block" disabled={!name.trim()}>
          {member ? "保存" : "下一步：录照片"}
        </button>
      </form>
    </Sheet>
  );
}
